import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Factory, MapPin, Eye, Edit } from 'lucide-react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useCrud } from '../hooks/useApi';
import { identityService } from '../services/api';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import FormModalLayout from '../components/Common/FormModalLayout';
import Can from '../components/Common/Can';
import { P } from '../constants/permissions';
import EnterpriseModal from '../components/Identity/EnterpriseModal';
import EnterpriseTable from '../components/Identity/EnterpriseTable';
import EnterpriseFilters from '../components/Identity/EnterpriseFilters';
import InteractiveMap from '../components/Map/InteractiveMap';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';
import { persistCompanyCnpj } from '../utils/cnpj';
import { useAuth } from '../contexts/AuthContext';
import { isRequesterRole } from '../utils/tenantProfile';
import { isOwnedEnterprise } from '../utils/portalScope';

function normalizeEnterprise(row) {
  if (!row) return row;
  return {
    ...row,
    name: row.name || row.nome || '',
    status: row.status || (row.is_active === false ? 'INACTIVE' : 'ACTIVE'),
  };
}

const TYPE_LABELS = {
  COMMERCIAL: 'Comercial',
  INDUSTRIAL: 'Industrial',
  RESIDENTIAL: 'Residencial',
};

/**
 * Página unificada de empreendimentos (rotas /enterprises e /identity/enterprises).
 */
const EnterpriseManagementPage = ({ breadcrumbs }) => {
  const { t } = useTranslation('enterprises');
  const { user } = useAuth();
  const requester = isRequesterRole(user);

  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('cards');
  const [showModal, setShowModal] = useState(false);
  const [editingEnterprise, setEditingEnterprise] = useState(null);
  const [viewEnterprise, setViewEnterprise] = useState(null);
  const [mapEnterprise, setMapEnterprise] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const { useList } = useCrud('identity-enterprises', identityService.enterpriseCrud);
  const { data: listData, isLoading, error, refetch } = useList();

  const enterprises = useMemo(() => {
    const raw = Array.isArray(listData) ? listData : listData?.data ?? listData?.items ?? [];
    const rows = (raw || []).map(normalizeEnterprise);
    if (!requester) return rows;
    return rows.filter((row) => isOwnedEnterprise(row, user));
  }, [listData, requester, user]);

  useEffect(() => {
    if (location.state?.openEnterpriseCreate) {
      setEditingEnterprise(null);
      setShowModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  /** Deep link: mapa / notificações abrem empreendimento (ex.: state.openEnterpriseId). */
  useEffect(() => {
    const id = location.state?.openEnterpriseId;
    if (!id) return;
    if (isLoading) return;
    const ent = enterprises.find((e) => String(e.id) === String(id));
    navigate(location.pathname, { replace: true, state: {} });
    if (ent) {
      setViewEnterprise(ent);
    } else {
      toast.error(t('notFound'));
    }
  }, [location.state, enterprises, isLoading, location.pathname, navigate]);

  useEffect(() => {
    const id = (searchParams.get('highlight') || '').trim();
    if (!id || isLoading) return;
    const ent = enterprises.find((e) => String(e.id) === String(id));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('highlight');
        return next;
      },
      { replace: true }
    );
    if (ent) setViewEnterprise(ent);
  }, [searchParams, enterprises, isLoading, setSearchParams]);

  const filteredEnterprises = enterprises.filter((enterprise) => {
    const name = (enterprise.name || '').toLowerCase();
    const cnpj = enterprise.cnpj || '';
    const matchesSearch =
      !filters.search ||
      name.includes(filters.search.toLowerCase()) ||
      cnpj.includes(filters.search);
    const matchesStatus = !filters.status || enterprise.status === filters.status;
    const matchesType = !filters.type || enterprise.type === filters.type;
    return matchesSearch && matchesStatus && matchesType;
  });

  const sortedEnterprises = [...filteredEnterprises].sort((a, b) => {
    const key = filters.sortBy;
    const aValue = String(a[key] ?? '');
    const bValue = String(b[key] ?? '');
    if (filters.sortOrder === 'asc') return aValue.localeCompare(bValue);
    return bValue.localeCompare(aValue);
  });

  const handleCreate = () => {
    setEditingEnterprise(null);
    setShowModal(true);
  };

  const handleEdit = (enterprise) => {
    setEditingEnterprise(enterprise);
    setShowModal(true);
  };

  const handleView = (enterprise) => {
    setViewEnterprise(enterprise);
  };

  const handleShowMap = (enterprise) => {
    setMapEnterprise(enterprise);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este empreendimento?')) return;
    try {
      await identityService.deleteEnterprise(id);
      toast.success('Empreendimento excluído');
      queryClient.invalidateQueries(['identity-enterprises']);
      refetch();
    } catch (e) {
      toast.error(userFacingError(e, 'deleteError'));
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editingEnterprise) {
        await identityService.updateEnterprise(editingEnterprise.id, {
          name: data.name,
          company_id: data.company_id,
          cnpj: data.cnpj || undefined,
          description: data.description || null,
          address: data.address || null,
          city: data.city || null,
          state: data.state || null,
          zip_code: data.zip_code || null,
          latitude: data.latitude === '' || data.latitude == null ? null : data.latitude,
          longitude: data.longitude === '' || data.longitude == null ? null : data.longitude,
          area_geojson: data.area_geojson,
        });
        await persistCompanyCnpj(
          data.company_id || editingEnterprise.company_id,
          data.cnpj,
          identityService,
        );
        toast.success('Empreendimento atualizado');
      } else if (requester) {
        await identityService.createEnterprise({
          name: data.name,
          owner_person_id: user?.person_id,
          company_id: data.company_id || user?.company_id || undefined,
          cnpj: data.cnpj || undefined,
          description: data.description || null,
          address: data.address || null,
          city: data.city || null,
          state: data.state || null,
          zip_code: data.zip_code || null,
          latitude: data.latitude === '' || data.latitude == null ? null : data.latitude,
          longitude: data.longitude === '' || data.longitude == null ? null : data.longitude,
          area_geojson: data.area_geojson,
        });
        toast.success('Empreendimento criado');
      } else {
        if (!data.inviteEmail?.trim()) {
          toast.error(t('inviteEmail'));
          return;
        }
        const inviteBody = {
          enterprise: {
            name: data.name,
            company_id: data.company_id,
            cnpj: data.cnpj || undefined,
            description: data.description || null,
            address: data.address || null,
            city: data.city || null,
            state: data.state || null,
            zip_code: data.zip_code || null,
            latitude: data.latitude,
            longitude: data.longitude,
            area_geojson: data.area_geojson,
          },
          invite_email: data.inviteEmail.trim(),
          login_with: 'email',
        };
        const res = await identityService.createEnterpriseWithAccessInvite(inviteBody).then((r) => r.data);
        await persistCompanyCnpj(data.company_id, data.cnpj, identityService);
        if (res.email_sent) {
          toast.success(t('createdInvite'));
        } else {
          toast.success(res.message || t('createdNoInvite'));
        }
      }
      setShowModal(false);
      setEditingEnterprise(null);
      queryClient.invalidateQueries(['identity-enterprises']);
      queryClient.invalidateQueries(['req-enterprises']);
      queryClient.invalidateQueries(['identity-companies-select']);
      queryClient.invalidateQueries(['identity-company']);
      refetch();
    } catch (e) {
      console.error(e);
      toast.error(userFacingError(e, 'saveError'));
    }
  };

  const getStatusBadge = (status) => {
    const active = status === 'ACTIVE';
    return (
      <span
        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
          active ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'
        }`}
      >
        {active ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const mapMarkers = useMemo(() => {
    if (!mapEnterprise) return [];
    const e = mapEnterprise;
    if (e.latitude == null || e.longitude == null) return [];
    return [
      {
        id: e.id,
        name: e.name || 'Empreendimento',
        type: (e.type || 'default').toLowerCase(),
        is_active: e.status === 'ACTIVE',
        description: e.description,
        address: e.address,
        city: e.city,
        state: e.state,
        latitude: Number(e.latitude),
        longitude: Number(e.longitude),
        area_geojson: e.area_geojson,
      },
    ];
  }, [mapEnterprise]);

  const mapCenter = useMemo(() => {
    if (!mapEnterprise || mapEnterprise.latitude == null || mapEnterprise.longitude == null) {
      return [-47.93, -15.78];
    }
    return [Number(mapEnterprise.longitude), Number(mapEnterprise.latitude)];
  }, [mapEnterprise]);

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
          <button type="button" className="btn btn-outline btn-sm mt-2" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description="Unidades cadastradas na identidade, com mapa e vínculo às licenças."
          breadcrumbs={breadcrumbs}
        >
          <Can any={[P.ENTERPRISES_VIEW, P.IDENTITY_ACCESS]}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Novo empreendimento
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <EnterpriseFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          <div className="p-5 sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
              </div>
            </div>

            {viewMode === 'table' ? (
              <EnterpriseTable
                enterprises={sortedEnterprises}
                isLoading={isLoading}
                onView={handleView}
                onShowMap={handleShowMap}
                onEdit={handleEdit}
                onDelete={requester ? undefined : handleDelete}
                onCreate={handleCreate}
              />
            ) : (
              <div>
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                  </div>
                ) : !sortedEnterprises.length ? (
                  <EnterpriseTable
                    enterprises={[]}
                    isLoading={false}
                    onCreate={handleCreate}
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {sortedEnterprises.map((enterprise) => (
                      <div key={enterprise.id} className="card">
                        <div className="card-content">
                          <div className="mb-4 flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900">{enterprise.name}</h3>
                              <p className="text-sm text-gray-600">{enterprise.cnpj || '—'}</p>
                            </div>
                            {getStatusBadge(enterprise.status)}
                          </div>
                          <div className="mb-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Tipo</span>
                              <span className="text-gray-900">
                                {TYPE_LABELS[enterprise.type] || enterprise.type || '—'}
                              </span>
                            </div>
                            {(enterprise.city || enterprise.state) && (
                              <div className="flex justify-between">
                                <span className="text-gray-500">Local</span>
                                <span className="text-gray-900">
                                  {[enterprise.city, enterprise.state].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                          <Can any={[P.ENTERPRISES_VIEW, P.IDENTITY_ACCESS]}>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-outline btn-sm flex-1"
                                onClick={() => handleView(enterprise)}
                              >
                                <Eye className="mr-1 h-4 w-4" />
                                Ver
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline btn-sm flex-1"
                                onClick={() => handleEdit(enterprise)}
                              >
                                <Edit className="mr-1 h-4 w-4" />
                                Editar
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                title="Localização"
                                onClick={() => handleShowMap(enterprise)}
                              >
                                <MapPin className="h-4 w-4" />
                              </button>
                            </div>
                          </Can>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <EnterpriseModal
          enterprise={editingEnterprise}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingEnterprise(null);
          }}
        />
      )}

      {viewEnterprise && (
        <FormModalLayout
          title={viewEnterprise.name || 'Empreendimento'}
          subtitle={t("detailsSubtitle")}
          headerIcon={Factory}
          historyEntityType="enterprises"
          historyEntityId={viewEnterprise?.id}
          onClose={() => setViewEnterprise(null)}
          size="2xl"
          footer={
            <button type="button" className="btn btn-outline" onClick={() => setViewEnterprise(null)}>
              Fechar
            </button>
          }
        >
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-gray-100 py-2">
              <dt className="text-gray-500">CNPJ</dt>
              <dd className="font-medium text-gray-900">{viewEnterprise.cnpj || '—'}</dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-2">
              <dt className="text-gray-500">Status</dt>
              <dd>{getStatusBadge(viewEnterprise.status)}</dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-2">
              <dt className="text-gray-500">Tipo</dt>
              <dd className="text-gray-900">
                {TYPE_LABELS[viewEnterprise.type] || viewEnterprise.type || '—'}
              </dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-2">
              <dt className="text-gray-500">Endereço</dt>
              <dd className="max-w-[60%] text-right text-gray-900">
                {viewEnterprise.address || '—'}
              </dd>
            </div>
            <div className="flex justify-between border-b border-gray-100 py-2">
              <dt className="text-gray-500">Cidade / UF</dt>
              <dd className="text-gray-900">
                {[viewEnterprise.city, viewEnterprise.state].filter(Boolean).join(', ') || '—'}
              </dd>
            </div>
            <div className="flex justify-between py-2">
              <dt className="text-gray-500">Coordenadas</dt>
              <dd className="font-mono text-xs text-gray-900">
                {viewEnterprise.latitude != null && viewEnterprise.longitude != null
                  ? `${Number(viewEnterprise.latitude).toFixed(6)}, ${Number(viewEnterprise.longitude).toFixed(6)}`
                  : '—'}
              </dd>
            </div>
          </dl>
        </FormModalLayout>
      )}

      {mapEnterprise && (
        <FormModalLayout
          title={`Localização — ${mapEnterprise.name || ''}`}
          subtitle={t("mapSubtitle")}
          headerIcon={MapPin}
          onClose={() => setMapEnterprise(null)}
          size="4xl"
          footer={
            <button type="button" className="btn btn-outline" onClick={() => setMapEnterprise(null)}>
              Fechar
            </button>
          }
        >
          {mapMarkers.length ? (
            <InteractiveMap
              enterprises={mapMarkers}
              center={mapCenter}
              zoom={14}
              height="400px"
              mapClassName="bg-gray-50"
              fitToPolygons
            />
          ) : (
            <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
              Este empreendimento não possui latitude e longitude cadastradas. Edite o cadastro e defina o ponto no
              mapa.
            </p>
          )}
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default EnterpriseManagementPage;
